import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { POPUP_CLASSNAME } from "constants/common.constants";

/** Distance the design leaves between the kebab and its menu. */
const GAP = 8;

/** Breathing room the menu keeps from the viewport edge. */
const EDGE = 12;

export type ShipmentRowAction = "open" | "tracking" | "remove";

type ShipmentRowMenuProps = {
  /** The kebab the menu hangs off; it is measured, never written to. */
  anchor: HTMLElement;
  /** Absent when the card has no number the tracking tool could open. */
  trackingUrl: string | null;
  onAction: (action: ShipmentRowAction) => void;
  onClose: () => void;
};

const OpenIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" aria-hidden="true">
    <rect x="4.2" y="4.4" width="15.6" height="15.2" rx="2.2" />
    <path d="M7.6 9.2h8.8M7.6 12.6h8.8M7.6 16h5.4" />
  </svg>
);

const TrackingIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" aria-hidden="true">
    <path d="M12 20.4s6.2-5.1 6.2-9.7A6.2 6.2 0 0 0 5.8 10.7c0 4.6 6.2 9.7 6.2 9.7z" />
    <circle cx="12" cy="10.6" r="2.2" />
  </svg>
);

const RemoveIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" aria-hidden="true">
    <path d="M4.6 7.2h14.8" />
    <path d="M9.4 7.2V5.4a1.2 1.2 0 0 1 1.2-1.2h2.8a1.2 1.2 0 0 1 1.2 1.2v1.8" />
    <path d="M6.6 7.2l.9 11.2a1.6 1.6 0 0 0 1.6 1.4h5.8a1.6 1.6 0 0 0 1.6-1.4l.9-11.2" />
  </svg>
);

/**
 * The row's three-dot menu. The design also lists Documents, View cargo and
 * Add note; the back office stores none of those, so only the three actions it
 * can carry out are offered.
 *
 * It renders into the node beside the app root, like `Modal`, because a menu
 * pinned to the viewport must not be trapped in a stacking context the panes
 * create further up.
 */
export const ShipmentRowMenu = ({ anchor, trackingUrl, onAction, onClose }: ShipmentRowMenuProps) => {
  const [host, setHost] = useState<HTMLElement | null>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const [at, setAt] = useState<{ left: number; top: number } | null>(null);

  useEffect(() => {
    setHost(document.querySelector<HTMLElement>(`.${POPUP_CLASSNAME}`));
  }, []);

  // Measured after paint: the menu has to exist before it can be placed, and
  // it flips above the kebab when there is no room underneath.
  useLayoutEffect(() => {
    const menu = menuRef.current;

    if (menu === null) return;

    const place = () => {
      const box = anchor.getBoundingClientRect();
      const width = menu.offsetWidth;
      const height = menu.offsetHeight;
      const left = Math.max(EDGE, Math.min(box.right - width, window.innerWidth - width - EDGE));
      const below = box.bottom + GAP;
      const top = below + height > window.innerHeight - EDGE ? Math.max(EDGE, box.top - height - GAP) : below;

      setAt({ left, top });
    };

    place();

    window.addEventListener("resize", onClose);
    window.addEventListener("scroll", place, true);

    return () => {
      window.removeEventListener("resize", onClose);
      window.removeEventListener("scroll", place, true);
    };
  }, [anchor, host, onClose]);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
    };

    // Capture: a click anywhere else closes the menu before that click is
    // read as a row click and opens a shipment behind it.
    const onClickAway = (event: MouseEvent) => {
      if (menuRef.current?.contains(event.target as Node) === true) return;
      if (anchor.contains(event.target as Node)) return;

      onClose();
    };

    document.addEventListener("keydown", onKeyDown);
    document.addEventListener("click", onClickAway, true);

    return () => {
      document.removeEventListener("keydown", onKeyDown);
      document.removeEventListener("click", onClickAway, true);
    };
  }, [anchor, onClose]);

  if (host === null) return null;

  return createPortal(
    <div
      ref={menuRef}
      className="pcshmn"
      role="menu"
      // Placed off-screen for the first frame rather than hidden, so it can be
      // measured before it is seen.
      style={at === null ? { left: -9999, top: 0 } : at}
    >
      <button type="button" className="pcshmn__i" role="menuitem" onClick={() => onAction("open")}>
        <i>
          <OpenIcon />
        </i>
        Open shipment
      </button>

      {trackingUrl !== null && (
        <button type="button" className="pcshmn__i" role="menuitem" onClick={() => onAction("tracking")}>
          <i>
            <TrackingIcon />
          </i>
          View tracking
        </button>
      )}

      <div className="pcshmn__sep" />

      <button type="button" className="pcshmn__i" role="menuitem" onClick={() => onAction("remove")}>
        <i>
          <RemoveIcon />
        </i>
        Remove from list
      </button>
    </div>,
    host
  );
};
